import { randomId } from "@codeshare/protocol";
import {
  type ChatMessage,
  type ChatWire,
  decodeWire,
  HISTORY_LIMIT,
  MAX_EMOJI_LENGTH,
  MAX_TEXT_LENGTH,
  type Reaction,
} from "./messages";
import type { ChatStore, ReactionSummary } from "./store";

export type Message = string | Uint8Array;

/** The slice of a room's channel API chat needs (structural, so chat stays DOM-free). */
export interface ChannelLike {
  send(message: Message): Promise<void>;
  onMessage(listener: (message: Message) => void): () => void;
  onClose(listener: () => void): () => void;
}

export interface RoomLike {
  onChannel(
    name: string,
    listener: (peerId: string, channel: ChannelLike, version: number) => void,
  ): () => void;
}

export interface ChatIdentity {
  clientId: string;
  name: string;
  color: string;
}

/** How long a "typing" indicator lasts without being refreshed. */
const TYPING_TTL_MS = 5_000;

/**
 * Room chat over cs.chat/1: send/receive messages, exchange history with every peer on connect
 * (merged by id, so it's safe in both directions and after offline periods), and typing state.
 */
export class Chat {
  readonly #store: ChatStore;
  readonly #channels = new Map<string, ChannelLike>();
  readonly #listeners = new Set<() => void>();
  readonly #typing = new Map<string, { name: string; until: number }>();
  readonly #cleanup: (() => void)[] = [];
  #identity: ChatIdentity;
  #clock: number;
  #typingTimer: ReturnType<typeof setTimeout> | undefined;

  constructor(options: { room: RoomLike; store: ChatStore; identity: ChatIdentity }) {
    this.#store = options.store;
    this.#identity = options.identity;
    this.#clock = Math.max(options.store.maxLamport, options.store.maxReactionLamport);
    this.#cleanup.push(
      options.room.onChannel("chat", (peerId, channel) => this.#attach(peerId, channel)),
    );
  }

  /** All messages, oldest first, in the same order on every peer. */
  messages(): readonly ChatMessage[] {
    return this.#store.all();
  }

  /** Other people currently typing. */
  typing(): { clientId: string; name: string }[] {
    const now = Date.now();
    return [...this.#typing]
      .filter(([clientId, t]) => t.until > now && clientId !== this.#identity.clientId)
      .map(([clientId, t]) => ({ clientId, name: t.name }));
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  setIdentity(identity: ChatIdentity): void {
    this.#identity = identity;
  }

  send(text: string): ChatMessage | undefined {
    const trimmed = text.trim().slice(0, MAX_TEXT_LENGTH);
    if (!trimmed) return undefined;
    this.#clock = Math.max(this.#clock, this.#store.maxLamport) + 1;
    const msg: ChatMessage = {
      id: randomId(),
      ...this.#identity,
      ts: Date.now(),
      lamport: this.#clock,
      text: trimmed,
    };
    this.#store.add([msg]);
    this.#broadcast({ t: "msg", msg });
    this.setTyping(false);
    this.#emit();
    return msg;
  }

  /** Message id → its reactions, grouped by emoji. */
  reactions(): Map<string, ReactionSummary[]> {
    return this.#store.reactionSummaries();
  }

  /** Adds this person's `emoji` to a message, or takes it back if it's already there. */
  react(msgId: string, emoji: string): void {
    if (!emoji || emoji.length > MAX_EMOJI_LENGTH) return;
    const { clientId, name } = this.#identity;
    const current = this.#store.reaction(msgId, clientId, emoji);
    this.#clock = Math.max(this.#clock, this.#store.maxLamport, this.#store.maxReactionLamport) + 1;
    const reaction: Reaction = {
      msgId,
      clientId,
      name,
      emoji,
      on: !current?.on,
      lamport: this.#clock,
    };
    this.#store.addReactions([reaction]);
    this.#broadcast({ t: "react", reaction });
    this.#emit();
  }

  setTyping(on: boolean): void {
    const { clientId, name } = this.#identity;
    this.#broadcast({ t: "typing", clientId, name, on });
  }

  destroy(): void {
    for (const fn of this.#cleanup.splice(0)) fn();
    clearTimeout(this.#typingTimer);
    this.#channels.clear();
  }

  #attach(peerId: string, channel: ChannelLike): void {
    this.#channels.set(peerId, channel);
    channel.onMessage((raw) => {
      if (typeof raw === "string") this.#receive(channel, raw);
    });
    channel.onClose(() => {
      if (this.#channels.get(peerId) === channel) this.#channels.delete(peerId);
    });
    // Catch up both ways: ask for their history (they'll ask for ours).
    void channel
      .send(JSON.stringify({ t: "history.req", since: 0 } satisfies ChatWire))
      .catch(() => {});
  }

  #receive(channel: ChannelLike, raw: string): void {
    const wire = decodeWire(raw);
    if (!wire) return;
    switch (wire.t) {
      case "msg":
        this.#merge([wire.msg]);
        this.#typing.delete(wire.msg.clientId);
        break;
      case "react":
        this.#mergeReactions([wire.reaction]);
        break;
      case "history.req": {
        const msgs = this.#store.recent(HISTORY_LIMIT);
        const reactions = this.#store.reactionsFor(new Set(msgs.map((m) => m.id)));
        void channel
          .send(JSON.stringify({ t: "history.res", msgs, reactions } satisfies ChatWire))
          .catch(() => {});
        break;
      }
      case "history.res":
        this.#merge(wire.msgs);
        if (wire.reactions) this.#mergeReactions(wire.reactions);
        break;
      case "typing":
        if (wire.on)
          this.#typing.set(wire.clientId, { name: wire.name, until: Date.now() + TYPING_TTL_MS });
        else this.#typing.delete(wire.clientId);
        this.#emit();
        this.#scheduleTypingExpiry();
        break;
    }
  }

  #merge(messages: ChatMessage[]): void {
    if (this.#store.add(messages) === 0) return;
    for (const m of messages) this.#clock = Math.max(this.#clock, m.lamport);
    this.#emit();
  }

  #mergeReactions(reactions: Reaction[]): void {
    if (this.#store.addReactions(reactions) === 0) return;
    for (const r of reactions) this.#clock = Math.max(this.#clock, r.lamport);
    this.#emit();
  }

  #broadcast(wire: ChatWire): void {
    const raw = JSON.stringify(wire);
    for (const channel of this.#channels.values()) void channel.send(raw).catch(() => {});
  }

  #scheduleTypingExpiry(): void {
    clearTimeout(this.#typingTimer);
    const next = Math.min(...[...this.#typing.values()].map((t) => t.until));
    if (!Number.isFinite(next)) return;
    this.#typingTimer = setTimeout(() => this.#emit(), Math.max(0, next - Date.now()) + 10);
  }

  #emit(): void {
    for (const listener of this.#listeners) listener();
  }
}
